import L from 'leaflet/dist/leaflet';
import 'leaflet/dist/leaflet.css';

export default class MapService {
    /* @ngInject */
    constructor($window, $log, $translate, AuthService) {
        this.$log = $log;
        this.$translate = $translate;
        this.leaflet = $window.leaflet;
        this.AuthService = AuthService;
        this.showDetailsLink = true;

        this.redIcon = new L.Icon({
            iconUrl: 'assets/red-marker.png',
            iconRetinaUrl: 'assets/red-marker-2x.png',
            shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
            iconSize: [25, 41],
            iconAnchor: [12, 41],
            popupAnchor: [1, -34],
            shadowSize: [41, 41],
        });
        this.blackIcon = new L.Icon({
            iconUrl: 'assets/black-marker.png',
            iconRetinaUrl: 'assets/black-marker-2x.png',
            shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
            iconSize: [25, 41],
            iconAnchor: [12, 41],
            popupAnchor: [1, -34],
            shadowSize: [41, 41],
        });
        this.greenIcon = new L.Icon({
            iconUrl: 'assets/green-marker.png',
            iconRetinaUrl: 'assets/green-marker-2x.png',
            shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
            iconSize: [25, 41],
            iconAnchor: [12, 41],
            popupAnchor: [1, -34],
            shadowSize: [41, 41],
        });

        this.map = null;
        this.featureGroup = null;
        this.traceLayer = null;
        this.traceStartMarker = null;

        // Synchronous translation lookups
        this.door_open = this.$translate.instant('IOF.DOOR_OPEN');
        this.door_closed = this.$translate.instant('IOF.DOOR_CLOSED');
        this.power_on = this.$translate.instant('IOF.POWER_ON');
        this.power_off = this.$translate.instant('IOF.POWER_OFF');
        this.details = this.$translate.instant('MAP.DETAILS');
        this.legendNormal = this.$translate.instant('MAP.LEGEND.NORMAL');
        this.legendAlarm = this.$translate.instant('MAP.LEGEND.ALARM');
        this.legendFirst = this.$translate.instant('MAP.LEGEND.FIRST');

        if (this.AuthService.isTransportChief()) {
            this.showDetailsLink = false;
        }
    }

    setupMap(mapId) {
        if ((this.map === undefined) || (this.map === null)) {
            this.map = L.map(mapId, {
                center: [56.30913, 15.46578],
                zoom: 10,
            });

            L.tileLayer('http://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
                {
                    attribution: '&copy; <a href="http://openstreetmap.org/copyright">OpenStreetMap</a> contributors',
                },
            ).addTo(this.map);

            this.featureGroup = new L.featureGroup();
            this.traceLayer = new L.featureGroup();
            this.traceStartMarker = new L.featureGroup();
        }
    }

    setupLegend() {
        const legend = L.control({ position: 'bottomleft' });
        legend.onAdd = () => {
            const div = L.DomUtil.create('div', 'sb-legend');
            div.innerHTML += `<div><img src="assets/black-marker.png" /> ${this.legendNormal}</div>`;
            div.innerHTML += `<div><img src="assets/red-marker.png" /> ${this.legendAlarm}</div>`;
            return div;
        };
        legend.addTo(this.map);
    }

    destroy() {
        if (this.map !== undefined && this.map !== null) {
            this.map.remove();
            this.map = null;
        }
    }

    showPopup(thingId) {
        this.featureGroup.eachLayer((l) => {
            // eslint-disable-next-line
            if (l._id === thingId) {
                l.openPopup();
            }
        });
    }

    showCartsOnMap(carts, mapId, showPopup = true, showLegend = true) {
        if (this.map === null) {
            this.setupMap(mapId);
            if (showLegend) {
                this.setupLegend();
            }
        }

        this.featureGroup.clearLayers();
        const markers = [];

        /* eslint-disable */
        carts.map((c) => {

            if (!c._source.state) {
                return;
            }

            const latlng = c._source.state.latlng;

            const door = c._source.state.door_open === 1 ? this.door_open : this.door_closed;
            const power = c._source.state.mains_powered === 1 ? this.power_on : this.power_off;

            let customPopup =
                `<div>
                    <h4><strong>${c._source.label}</strong></h4>
                    <h5>${c._source.state.box_temperature}&#8451;</h5>
                    <h5>${door}</h5>
                    <h5>${power}</h5>`
            if (this.showDetailsLink) {
                customPopup = customPopup + `<a class="btn btn-primary btn-block" href="#!/detail/${c._id}" style="text-decoration:none;">${this.details}</a>
                <a class="leaflet-popup-close-button sb-close-overide btn btn-default btn-block" href="#close">Stäng</a></div>`;
            } else {
                customPopup = customPopup + `<a class="leaflet-popup-close-button sb-close-overide btn btn-default btn-block" href="#close">Stäng</a></div>`;
            }

            const customPopupOptions = {
                minWidth: '250',
                className: 'customPopupClass',
            };

            if (latlng) {
                const lat = latlng.split(',')[0].trim();
                const lng = latlng.split(',')[1].trim();

                let marker = null;
                if (showPopup) {
                    marker = L.marker([lat, lng], { icon: c.hasAlarms ? this.redIcon : this.blackIcon })
                        .bindPopup(customPopup, customPopupOptions)
                        .addTo(this.map);
                } else {
                    marker = L.marker([lat, lng], { icon: this.blackIcon })
                        .addTo(this.map);
                }
                marker._id = c._id !== undefined ? c._id : '';
                markers.push(marker);
            }
        },
            /* eslint-enable */
        );

        if (markers.length > 0) {
            this.featureGroup = new L.featureGroup(markers);
            this.map.addLayer(this.featureGroup);
            this.map.fitBounds(this.featureGroup.getBounds().pad(0.05), { maxZoom: 18 });
        }
    }

    showCartTrace(data, mapId) {
        if (this.map === null) {
            this.setupMap(mapId);
            this.setupLegend(true);
        }

        this.traceLayer.clearLayers();
        this.traceStartMarker.clearLayers();

        /* eslint-disable */
        const pointList = [];
        let startMarker = null;
        let first = true;


        for (let i = 0; i < data.length; i++) {

            if (data[i].latlng && data[i].latlng !== undefined) {
                // ensure correct data
                if (typeof data[i].latlng === 'string') {
                    try {
                        let A1 = parseFloat(data[i].latlng.split(',')[0].trim());
                        let A2 = parseFloat(data[i].latlng.split(',')[1].trim());

                        let pointA = new L.LatLng(A1, A2);
                        pointList.push(pointA);

                        if (first === true) {
                            startMarker = new L.marker(pointA, { icon: this.greenIcon }).addTo(this.map);
                            first = false;
                        }
                    } catch (error) {
                        console.log(error);
                    }
                }

            }
        }
        /* eslint-enable */

        if (startMarker !== null) {
            // // this.map.addLayer(startMarker);
            this.traceStartMarker = new L.featureGroup(startMarker);
            this.map.addLayer(this.traceStartMarker);
        }

        if (pointList.length > 0) {
            const polyLine = new L.Polyline(pointList, {
                color: 'black',
                weight: 7,
                opacity: 0.5,
                smoothFactor: 1,
            });

            this.map.addLayer(polyLine);
            this.map.fitBounds(polyLine.getBounds().pad(0.02), { maxZoom: 18 });
        }
    }

    clearMap() {
        /* eslint-disable */
        if (this.map) {
            for (let i in this.map._layers) {
                if (this.map._layers[i]._path != undefined) {
                    try {
                        this.map.removeLayer(this.map._layers[i]);
                    }
                    catch (e) {
                        console.log("problem with " + e + this.map._layers[i]);
                    }
                }
            }
        }

        /* eslint-enable */
    }
}



// WEBPACK FOOTER //
// ./src/app/services/map.service.js